import {
  motion,
  useAnimationFrame,
  useMotionValue,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform,
  useVelocity,
} from 'motion/react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { PiAsteriskBold } from 'react-icons/pi';

const wrap = (min: number, max: number, v: number) => {
  const r = max - min;
  return ((((v - min) % r) + r) % r) + min;
};

/**
 * Single kinetic capabilities strip (max one marquee per page). It drifts on
 * its own, accelerates and flips direction with scroll velocity, and leans
 * into the motion with a skew. Static under reduced motion.
 */
export function Marquee() {
  const { t } = useTranslation('common');
  const reduce = useReducedMotion();
  const words = t('marquee', { returnObjects: true }) as string[];

  const baseX = useMotionValue(0);
  const { scrollY } = useScroll();
  const velocity = useSpring(useVelocity(scrollY), {
    damping: 50,
    stiffness: 400,
  });
  const boost = useTransform(velocity, [0, 1000], [0, 4], { clamp: false });
  const skewX = useTransform(velocity, [-2500, 2500], [10, -10]);
  const spin = useTransform(baseX, (v) => v * 18);
  const direction = useRef(-1);

  useAnimationFrame((_, delta) => {
    if (reduce) return;
    const b = boost.get();
    if (b < 0) direction.current = 1;
    else if (b > 0) direction.current = -1;
    const move = direction.current * 2.2 * (delta / 1000);
    baseX.set(baseX.get() + move + move * Math.abs(b));
  });

  const x = useTransform(baseX, (v) => `${wrap(-50, 0, v)}%`);

  const row = (hidden: boolean) => (
    <div aria-hidden={hidden} className="flex shrink-0 items-center">
      {words.map((w, i) => (
        <span key={w} className="flex items-center">
          <span
            className={`px-6 font-display text-5xl font-semibold tracking-[-0.04em] md:px-10 md:text-8xl ${
              i % 2 ? 'text-outline' : 'text-fg'
            }`}
          >
            {w}
          </span>
          <motion.span
            style={{ rotate: reduce ? 0 : spin }}
            className="inline-flex"
          >
            <PiAsteriskBold className="shrink-0 text-2xl text-accent md:text-4xl" />
          </motion.span>
        </span>
      ))}
    </div>
  );

  return (
    <section
      aria-label="Capabilities"
      className="relative overflow-hidden py-14 md:py-20"
    >
      <motion.div
        className="flex w-max"
        style={reduce ? undefined : { x, skewX }}
      >
        {row(false)}
        {row(true)}
      </motion.div>
    </section>
  );
}
